import { useEffect, useMemo, useRef } from 'react'
import { gsap, reducedMotion } from '@/lib/anim'

/**
 * Double-helix strand drawn as fine SVG strokes.
 * Vertical by default; draws itself in on scroll (stroke-dashoffset),
 * like a line being traced through the page.
 */
export function HelixStrand({
  height = 480,
  width = 90,
  turns = 2.5,
  stroke = '#8BA872',
  rungStroke,
  opacity = 0.5,
  strokeWidth = 1,
  animate = true,
  rungs = 9,
  className = '',
}: {
  height?: number
  width?: number
  turns?: number
  stroke?: string
  rungStroke?: string
  opacity?: number
  strokeWidth?: number
  animate?: boolean
  rungs?: number
  className?: string
}) {
  const ref = useRef<SVGSVGElement>(null)

  const { d1, d2, rungLines } = useMemo(() => {
    const cx = width / 2
    const amp = width / 2 - strokeWidth * 2
    const steps = 120
    const p1: string[] = []
    const p2: string[] = []
    for (let i = 0; i <= steps; i++) {
      const t = i / steps
      const y = t * height
      const a = t * turns * Math.PI * 2
      p1.push(`${i === 0 ? 'M' : 'L'}${(cx + amp * Math.sin(a)).toFixed(2)},${y.toFixed(2)}`)
      p2.push(`${i === 0 ? 'M' : 'L'}${(cx + amp * Math.sin(a + Math.PI)).toFixed(2)},${y.toFixed(2)}`)
    }
    const rl: { x1: number; x2: number; y: number; o: number }[] = []
    for (let i = 1; i <= rungs; i++) {
      const t = i / (rungs + 1)
      const y = t * height
      const a = t * turns * Math.PI * 2
      const x1 = cx + amp * Math.sin(a)
      const x2 = cx + amp * Math.sin(a + Math.PI)
      // rungs facing the viewer are brighter, back rungs faint
      const facing = Math.abs(Math.cos(a))
      rl.push({ x1, x2, y, o: 0.15 + facing * 0.5 })
    }
    return { d1: p1.join(' '), d2: p2.join(' '), rungLines: rl }
  }, [height, width, turns, rungs, strokeWidth])

  useEffect(() => {
    if (!animate || reducedMotion || !ref.current) return
    const paths = ref.current.querySelectorAll('path')
    const lines = ref.current.querySelectorAll('line')
    paths.forEach((p) => {
      const len = (p as SVGPathElement).getTotalLength()
      p.style.strokeDasharray = `${len}`
      p.style.strokeDashoffset = `${len}`
    })
    lines.forEach((l) => {
      const len = Math.abs(Number(l.getAttribute('x2')) - Number(l.getAttribute('x1')))
      l.style.strokeDasharray = `${len}`
      l.style.strokeDashoffset = `${len}`
    })
    const tl = gsap.timeline({
      scrollTrigger: { trigger: ref.current, start: 'top 88%', once: true },
    })
    tl.to(paths, { strokeDashoffset: 0, duration: 2.2, ease: 'power2.inOut', stagger: 0.15 })
      .to(lines, { strokeDashoffset: 0, duration: 1.2, ease: 'power2.out', stagger: 0.06 }, '-=1.6')
    return () => {
      tl.scrollTrigger?.kill()
      tl.kill()
    }
  }, [animate])

  return (
    <svg
      ref={ref}
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      fill="none"
      className={className}
      style={{ opacity }}
      aria-hidden="true"
    >
      <path d={d1} stroke={stroke} strokeWidth={strokeWidth} />
      <path d={d2} stroke={stroke} strokeWidth={strokeWidth} />
      {rungLines.map((r, i) => (
        <line
          key={i}
          x1={r.x1}
          x2={r.x2}
          y1={r.y}
          y2={r.y}
          stroke={rungStroke ?? stroke}
          strokeWidth={strokeWidth * 0.75}
          opacity={r.o}
        />
      ))}
    </svg>
  )
}

/**
 * Horizontal helix band — a quiet divider that draws itself across
 * the section boundary. One strand in green, one in amber.
 */
export function HelixBand({
  width = 1240,
  height = 56,
  turns = 6,
  className = '',
  dark = false,
}: {
  width?: number
  height?: number
  turns?: number
  className?: string
  dark?: boolean
}) {
  const ref = useRef<SVGSVGElement>(null)

  const { d1, d2, ticks } = useMemo(() => {
    const cy = height / 2
    const amp = height / 2 - 3
    const steps = 160
    const p1: string[] = []
    const p2: string[] = []
    for (let i = 0; i <= steps; i++) {
      const t = i / steps
      const x = t * width
      const a = t * turns * Math.PI * 2
      p1.push(`${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${(cy + amp * Math.sin(a)).toFixed(2)}`)
      p2.push(`${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${(cy + amp * Math.sin(a + Math.PI)).toFixed(2)}`)
    }
    const tk: { y1: number; y2: number; x: number; o: number }[] = []
    const n = turns * 4
    for (let i = 0; i <= n; i++) {
      const t = i / n
      const x = t * width
      const a = t * turns * Math.PI * 2
      const y1 = cy + amp * Math.sin(a)
      const y2 = cy + amp * Math.sin(a + Math.PI)
      tk.push({ x, y1, y2, o: 0.12 + Math.abs(Math.cos(a)) * 0.4 })
    }
    return { d1: p1.join(' '), d2: p2.join(' '), ticks: tk }
  }, [width, height, turns])

  useEffect(() => {
    if (reducedMotion || !ref.current) return
    const paths = ref.current.querySelectorAll('path')
    paths.forEach((p) => {
      const len = (p as SVGPathElement).getTotalLength()
      p.style.strokeDasharray = `${len}`
      p.style.strokeDashoffset = `${len}`
    })
    const lines = ref.current.querySelectorAll('line')
    gsap.set(lines, { opacity: 0 })
    const tl = gsap.timeline({
      scrollTrigger: { trigger: ref.current, start: 'top 92%', once: true },
    })
    tl.to(paths, { strokeDashoffset: 0, duration: 2.6, ease: 'power1.inOut', stagger: 0.2 }).to(
      lines,
      { opacity: (i) => ticks[i]?.o ?? 0.3, duration: 0.8, stagger: 0.02 },
      '-=1.8',
    )
    return () => {
      tl.scrollTrigger?.kill()
      tl.kill()
    }
  }, [ticks])

  const green = dark ? 'rgba(139,168,114,0.55)' : 'rgba(74,107,58,0.45)'
  const amber = dark ? 'rgba(232,184,92,0.5)' : 'rgba(201,142,51,0.45)'

  return (
    <svg
      ref={ref}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className={className}
      style={{ width: '100%', height }}
      fill="none"
      aria-hidden="true"
    >
      <path d={d1} stroke={green} strokeWidth={1} vectorEffect="non-scaling-stroke" />
      <path d={d2} stroke={amber} strokeWidth={1} vectorEffect="non-scaling-stroke" />
      {ticks.map((t, i) => (
        <line
          key={i}
          x1={t.x}
          x2={t.x}
          y1={t.y1}
          y2={t.y2}
          stroke={dark ? 'rgba(243,245,241,0.25)' : 'rgba(86,86,80,0.2)'}
          strokeWidth={0.75}
          vectorEffect="non-scaling-stroke"
        />
      ))}
    </svg>
  )
}
